import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Stack, Chip, Button, Avatar,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, InputAdornment, CircularProgress, Alert,
} from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_DARK, PRIMARY_BORDER } from '../../theme';
import { inr, initialsFromName as initials, avatarTone } from '../../utils/format';

const solidBtn = { bgcolor: PRIMARY, color: '#fff', fontWeight: 700, borderRadius: '7px', boxShadow: 'none', textTransform: 'none', '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: 'none' } };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtMonth = (ym) => { if (!ym) return '—'; const [y, m] = ym.split('-'); return `${MONTHS[+m - 1]} ${y}`; };
const addMonths = (ym, n) => { const [y, m] = ym.split('-').map(Number); const d = new Date(y, m - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const thisMonth = () => new Date().toISOString().slice(0, 7);

const QUICK_MONTHS = [3, 6, 9, 12];
const MAX_MONTHS = 12;

const START_MONTH_OFFSETS = [1, 2, 3];
const startMonthChoices = () => START_MONTH_OFFSETS.map((n) => addMonths(thisMonth(), n));

const EMPTY_FORM = () => ({ amount: '', reason: '', plan: 'installment', months: '', startMonth: startMonthChoices()[0] });

export default function RequestAdvanceDialog({ open, onClose, me, saving = false, onSubmit, onInvalid }) {
    const [form, setForm] = useState(EMPTY_FORM);
    const [tried, setTried] = useState(false);

    useEffect(() => {
        if (open) { setForm(EMPTY_FORM()); setTried(false); }
    }, [open]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const setMonths = (e) => {
        const digits = e.target.value.replace(/\D/g, '');
        if (digits === '') { setForm((f) => ({ ...f, months: '' })); return; }
        const num = Number(digits);
        if (num < 1 || num > MAX_MONTHS) return;
        setForm((f) => ({ ...f, months: String(num) }));
    };

    const amountNum = Number(form.amount) || 0;
    const months = form.plan === 'onetime' ? 1 : (Number(form.months) || 0);
    const monthlyNum = form.plan === 'onetime'
        ? amountNum
        : (months > 0 && amountNum > 0 ? Math.ceil(amountNum / months) : 0);
    const endMonth = months > 0 ? addMonths(form.startMonth, months - 1) : form.startMonth;
    const lastMonthNum = months > 0 ? amountNum - monthlyNum * (months - 1) : 0;

    const monthChoices = startMonthChoices();
    const knowsRequester = Boolean(me?.employeeId || me?.employeeName);

    const formValid = amountNum > 0
        && knowsRequester
        && monthChoices.includes(form.startMonth)
        && (form.plan === 'onetime' || (months >= 1 && months <= MAX_MONTHS));

    const handleSubmit = () => {
        setTried(true);
        if (!formValid) { onInvalid?.(); return; }
        onSubmit({
            amount: amountNum,
            plan: form.plan,
            months,
            monthlyAmount: monthlyNum,
            startMonth: form.startMonth,
            reason: (form.reason || '').trim(),
        });
    };

    return (
        <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ pb: 1 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>Request Salary Advance</Typography>
                <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>Choose an amount and how it will be deducted from monthly salary.</Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
                {knowsRequester ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, p: 1.5, borderRadius: '9px', bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY_BORDER}`, mb: 2 }}>
                        <Avatar sx={{ width: 38, height: 38, ...avatarTone(me.employeeName), fontSize: 13, fontWeight: 700 }}>{initials(me.employeeName)}</Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0F172A' }}>{me.employeeName || me.employeeId}</Typography>
                            <Typography sx={{ fontSize: 11.5, color: PRIMARY_DARK }}>
                                {[me.employeeId, me.department].filter(Boolean).join(' · ')}{(me.employeeId || me.department) ? ' · ' : ''}requesting for yourself
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: '9px', fontSize: 12.5, py: 0.5 }}>
                        Your employee profile isn't linked to this login, so the request can't be attributed. Please sign in again or contact HR.
                    </Alert>
                )}

                <TextField
                    label="Advance amount" type="number" size="small" fullWidth value={form.amount}
                    onChange={set('amount')} error={tried && amountNum <= 0}
                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    sx={{ mb: 2 }}
                />

                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#374151', mb: 1 }}>Deduction plan</Typography>
                <Stack direction="row" spacing={1.2} sx={{ mb: 2 }}>
                    {[
                        { key: 'installment', label: 'Monthly instalment', desc: 'A fixed amount every month', icon: EventRepeatRoundedIcon },
                        { key: 'onetime', label: 'One-time', desc: 'Full amount in one month', icon: PaymentsRoundedIcon },
                    ].map((opt) => {
                        const sel = form.plan === opt.key;
                        return (
                            <Box key={opt.key} onClick={() => setForm((f) => ({ ...f, plan: opt.key }))}
                                sx={{ flex: 1, cursor: 'pointer', p: 1.5, borderRadius: '9px', border: `1.5px solid ${sel ? PRIMARY : '#E5E7EB'}`, bgcolor: sel ? PRIMARY_LIGHT : '#fff', transition: '0.15s' }}>
                                <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', mb: 0.3 }}>
                                    <opt.icon sx={{ fontSize: 18, color: sel ? PRIMARY : '#94A3B8' }} />
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: sel ? PRIMARY_DARK : '#374151' }}>{opt.label}</Typography>
                                </Stack>
                                <Typography sx={{ fontSize: 11, color: '#6B7280' }}>{opt.desc}</Typography>
                            </Box>
                        );
                    })}
                </Stack>

                {form.plan === 'installment' && (
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            label="Number of months" size="small" fullWidth value={form.months}
                            onChange={setMonths} error={tried && !(months >= 1 && months <= MAX_MONTHS)}
                            helperText={tried && !(months >= 1 && months <= MAX_MONTHS) ? `Enter a number between 1 and ${MAX_MONTHS}.` : ' '}
                            inputProps={{ inputMode: 'numeric', maxLength: 2 }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><EventRepeatRoundedIcon sx={{ fontSize: 18, color: '#94A3B8' }} /></InputAdornment> }}
                        />
                        <Stack direction="row" spacing={0.8} sx={{ mt: 0.2, mb: 2, flexWrap: 'wrap', gap: 0.8 }}>
                            {QUICK_MONTHS.map((m) => (
                                <Chip key={m} label={`${m} months`} size="small" onClick={() => setForm((f) => ({ ...f, months: String(m) }))}
                                    sx={{ cursor: 'pointer', fontWeight: 700, fontSize: 11.5, bgcolor: months === m ? PRIMARY : '#F1F5F9', color: months === m ? '#fff' : '#475569', '&:hover': { bgcolor: months === m ? PRIMARY_DARK : '#E2E8F0' } }} />
                            ))}
                        </Stack>
                        <TextField
                            label="Deduction per month" size="small" fullWidth disabled
                            value={monthlyNum > 0 ? String(monthlyNum) : ''}
                            helperText={months > 0 && amountNum > 0 ? `${inr(amountNum)} ÷ ${months} month${months > 1 ? 's' : ''}` : 'Auto-calculated from the amount and months.'}
                            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                        />
                    </Box>
                )}

                <TextField
                    select label="Start from month" size="small" fullWidth value={form.startMonth}
                    onChange={set('startMonth')} error={tried && !monthChoices.includes(form.startMonth)}
                    helperText={`Recovery can start from ${fmtMonth(monthChoices[0])} up to ${fmtMonth(monthChoices[monthChoices.length - 1])}.`}
                    InputProps={{ startAdornment: <InputAdornment position="start"><CalendarMonthRoundedIcon sx={{ fontSize: 18, color: '#94A3B8' }} /></InputAdornment> }}
                    sx={{ mb: 2 }}
                >
                    {monthChoices.map((m) => (
                        <MenuItem key={m} value={m} sx={{ fontSize: 13.5 }}>{fmtMonth(m)}</MenuItem>
                    ))}
                </TextField>

                <TextField label="Notes / Remarks (optional)" size="small" fullWidth multiline minRows={2} value={form.reason} onChange={set('reason')} sx={{ mb: 1 }} />

                {amountNum > 0 && (form.plan === 'onetime' || monthlyNum > 0) && (
                    <Box sx={{ p: 1.6, borderRadius: '9px', bgcolor: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#15803D', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.4 }}>Recovery preview</Typography>
                        {form.plan === 'onetime' ? (
                            <Typography sx={{ fontSize: 12.5, color: '#166534' }}>
                                Full <strong>{inr(amountNum)}</strong> deducted from <strong>{fmtMonth(form.startMonth)}</strong> salary.
                            </Typography>
                        ) : (
                            <Typography sx={{ fontSize: 12.5, color: '#166534' }}>
                                <strong>{inr(amountNum)}</strong> recovered over <strong>{months} month{months > 1 ? 's' : ''}</strong> — {inr(monthlyNum)}/month from <strong>{fmtMonth(form.startMonth)}</strong> to <strong>{fmtMonth(endMonth)}</strong>
                                {lastMonthNum !== monthlyNum ? ` (last month ${inr(lastMonthNum)}).` : '.'}
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#64748B', borderRadius: '7px' }}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={saving} startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 40, px: 2.4 }}>
                    {saving ? 'Sending…' : 'Request Advance'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
